<script setup lang="ts">
import { defineProps, reactive, ref, Ref, watch, computed } from "vue";
import {
  formatDateTime,
} from "@/services/functions";
import AlertModal from "@/components/Common/AlertModal.vue";
import HeaderRow from "@/components/MonthlyReportWorkingTimesSettingParts/TableParts/HeaderRow.vue";
import TripRow from "@/components/MonthlyReportWorkingTimesSettingParts/TableParts/TripRow.vue";
import EmptyRow from "@/components/MonthlyReportWorkingTimesSettingParts/TableParts/EmptyRow.vue";
import WeeklyAggregationRow from "@/components/MonthlyReportWorkingTimesSettingParts/TableParts/WeeklyAggregationRow.vue";
import i18n from '@/lang/i18n';

type Props = {
  detailData?: any;
  alertSetting?: any;
  monthlyTrips?: any;
  maxWorkingTime?: any;
  displayWeeklyAggregation?: boolean;
  btnState?: boolean;
  trips: any
};

const props = defineProps<Props>();
const detailDataRef = reactive<Array<any>>(props.detailData);
const alertModalTitle = ref("");
const alertModalMessage = ref("");
const modalState = ref(false);

const validationResults:Ref<{[key:number]: boolean}> = ref({})

const setValidationResult = (key: string, value: boolean) => {
  validationResults.value[key] = value;
}

type Emits = {
  (e: "changeWorkingTime", value: any): void;
  (e: "clickSave", value: any): void;
};
const emit = defineEmits<Emits>();

watch(
  () => props.btnState,
  () => {
    if (!props.btnState) return;

    const invalid = Object.values(validationResults.value).includes(false);
    if (invalid) {
      emit("clickSave", {valid: !invalid, workingTimes: []});
      return ;
    }

    const workingTimes = [];
    detailDataRef.forEach((detailData) => {
      (detailData?.trips ?? []).forEach((trip) => {
        workingTimes.push({
          delivery_plan_id: trip.delivery_plan_id,
          sub_id: trip.sub_id,
          start_at: formatDateTime(new Date(trip.csv_start_working_time)),
          end_at: formatDateTime(new Date(trip.csv_end_working_time)),
          is_ittenko_cooperation: {
            start: trip?.is_ittenko_cooperation?.start,
            end: trip?.is_ittenko_cooperation?.end,
          },
        });
      });
    });

    return emit("clickSave", { valid: !invalid, workingTimes: workingTimes });
  }
);

const getRowSpan = (trips: any) => {
  return trips.length > 1 ? 1 : 2;
};

const convertDayOfWeekToJa = (dayOfWeekEn: string) => {
  return {
    Sun: i18n.global.t('pages.common.weeks.Sun'),
    Mon: i18n.global.t('pages.common.weeks.Mon'),
    Tue: i18n.global.t('pages.common.weeks.Tue'),
    Wed: i18n.global.t('pages.common.weeks.Wed'),
    Thu: i18n.global.t('pages.common.weeks.Thu'),
    Fri: i18n.global.t('pages.common.weeks.Fri'),
    Sat: i18n.global.t('pages.common.weeks.Sat'),
  }[dayOfWeekEn];
};

function showAlert(message: string) {
  alertModalTitle.value = i18n.global.t('pages.monthlyReportWorkingTimesSetting.inputError');
  alertModalMessage.value = message;
  modalState.value = true;
}

const closeAlert = () => {
  alertModalTitle.value = "";
  alertModalMessage.value = "";
  modalState.value = false;
};

const getDayRowSpan = (trips) => {
  const length = trips.length;
  return length == 0 ? 1 : length;
};

const getTrip = (dayIndex, tripIndex, direction) => {
  let trips = detailDataRef[dayIndex]?.trips ?? [];
  let target = trips[tripIndex + direction];
  if (target) return target;

  const len = detailDataRef.length;
  for (let i = dayIndex + direction; i >= 0 && i < len; i += direction) {
    if (!detailDataRef[i]?.trips.length) continue;
    trips = detailDataRef[i]?.trips;
    const index = direction < 0 ? trips.length - 1 : 0;
    target = trips[index];
    break;
  }
  return target;
};

const cloneTrip = (trip) => {
  return JSON.parse(JSON.stringify(trip ?? "{}"));
};

const makeLeavingDiffTime = computed(() => (detailIndex: number, tripIndex: number) => {
  const isLastDay = detailDataRef[detailIndex + 1] == undefined
  if (!isLastDay && (tripIndex != 0 || !detailDataRef[detailIndex + 1]?.trips.length)) return 0

  const appointedDay = new Date(detailDataRef[detailIndex].trips[tripIndex].csv_start_working_time)
  const AddedDay = appointedDay.setDate(appointedDay.getDate() + 1)

  let nextDayLeavingTime
  if (isLastDay) {
    nextDayLeavingTime = props.trips[props.trips.length - 1].csv_start_working_time
  } else {
    nextDayLeavingTime = detailDataRef[detailIndex + 1].trips[0].csv_start_working_time
  }

  const diffSeconds = (AddedDay - new Date(nextDayLeavingTime)) / 1000
  return diffSeconds < 0 ? 0 : diffSeconds
})
</script>

<template>
  <div class="row clearfix pt-0 row">
    <div class="col-md-12">
      <table id="trip-detail-table">
        <HeaderRow />

        <template
          v-for="(detailData, detail_i) in detailDataRef"
          :key="detail_i"
        >
          <!-- exist trip day row -->
          <template v-if="detailData.trips.length">
            <template v-for="(trip, trip_i) in detailData.trips" :key="trip_i">
              <tr>
                <template v-if="trip_i == 0">
                  <td :rowspan="getDayRowSpan(detailData.trips)">
                    {{ detailData.day }}
                  </td>
                  <td :rowspan="getDayRowSpan(detailData.trips)">
                    {{ convertDayOfWeekToJa(detailData.dayOfWeek) }}
                  </td>
                </template>
                <TripRow
                  v-if="detailData.trips.length"
                  :rowSpan="getRowSpan(detailData.trips)"
                  :beforeTrip="getTrip(detail_i, trip_i, -1)"
                  :trip="cloneTrip(props.detailData[detail_i].trips[trip_i])"
                  :afterTrip="getTrip(detail_i, trip_i, 1)"
                  v-model="detailDataRef[detail_i].trips[trip_i]"
                  :maxWorkingTime="maxWorkingTime"
                  :alertSetting="alertSetting"
                  :showAlert="showAlert"
                  :setValidationResult="setValidationResult"
                  :leavingDiffTime="makeLeavingDiffTime(detail_i, trip_i)"
                />
              </tr>
            </template>
          </template>
          <template v-else>
            <tr>
              <td :rowspan="getDayRowSpan(detailData.trips)">
                {{ detailData.day }}
              </td>
              <td :rowspan="getDayRowSpan(detailData.trips)">
                {{ convertDayOfWeekToJa(detailData.dayOfWeek) }}
              </td>
              <EmptyRow :detailData="detailData" :rowSpan="2" />
            </tr>
          </template>

          <tr v-show="!detailData.trips.length"></tr>
          <tr v-show="detailData.trips.length == 1"></tr>

          <!-- sub total row -->
          <WeeklyAggregationRow
            :detailDataRef="detailDataRef"
            :index="detail_i"
            :detailData="detailData"
            :alertSetting="alertSetting"
          />
        </template>
      </table>
    </div>
    <AlertModal
      :modalState="modalState"
      :title="alertModalTitle"
      :subTitle="alertModalMessage"
      modalMaxWidth="60%"
      @closeModal="closeAlert"
    />
  </div>
</template>

<style scoped>
#trip-detail-table td {
  padding-top: 7px;
  padding-bottom: 7px;
  padding-left: 2px;
  padding-right: 2px;
  text-align: center;
  font-weight: bold !important;
  border: 1 !important;
  border-width: 2px !important;
}
#trip-detail-table .table-header {
  background-color: silver !important;
}
#trip-detail-table tr,
p {
  line-height: 15px;
  font-size: 13px;
  font-weight: bold;
}
.nothing-col {
  opacity: 0;
  border-top: none !important;
  border-bottom: none !important;
  border-right: none !important;
}

.working-time-color-black {
  color: black;
}

.working-time-color-blue {
  color: blue;
}
</style>
